import { Link, useLocation } from "react-router-dom";
import { useFocus } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatTile, StatusPill } from "../components/ui";
import { QUOTA_REQUEST_STATUS } from "../lib/quota-requests";
import { useFormat } from "../lib/useFormat";

export function FocusPage() {
  const fmt = useFormat();
  const { me } = useAuth();
  const focus = useFocus();
  const fallback = (useLocation().state as { landingFallback?: string } | null)?.landingFallback;
  if (focus.isPending) return <LoadingState label="Loading what needs attention" />;
  if (focus.isError) return <ErrorState error={focus.error} onRetry={() => void focus.refetch()} />;
  const f = focus.data;
  const internal = me?.role === "internal-team";

  return (
    <>
      <PageHeader title="Focus" subtitle={`What needs attention today · generated ${fmt.time(f.generatedAt)}`} />
      {fallback && (
        <p role="status" className="mb-4 rounded border border-neutral-200 bg-neutral-50 p-3 text-sm dark:border-neutral-800 dark:bg-neutral-900">
          Your chosen start page ({fallback}) is no longer available to you, so you are on Focus. <Link to="/account" className="underline">Pick another</Link>.
        </p>
      )}
      <div className={`grid gap-4 ${internal ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
        <StatTile label="Critical alarms open" value={f.tiles.criticalAlarmsOpen} tone={f.tiles.criticalAlarmsOpen ? "critical" : "ok"} />
        <StatTile label="Quotas at 80%+" value={f.tiles.quotasNearLimit} tone={f.tiles.quotasNearLimit ? "warning" : "ok"} />
        {internal && <StatTile label="Accounts with no heartbeat" value={f.tiles.accountsGoneQuiet ?? 0} tone={f.tiles.accountsGoneQuiet ? "warning" : "ok"} to="/customers" />}
        <StatTile label="Open support cases" value={f.tiles.openSupportCases} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card title="Critical alarms">
          <DataTable
            caption="Open critical alarms"
            rows={f.criticalAlarms}
            rowKey={(a) => `${a.environmentId}:${a.alarmArn}`}

            sortBy={(a) => a.alarmName}
            empty={<EmptyState title="No open critical alarms" hint="Nothing critical is firing right now." />}
            columns={[
              { key: "alarm", header: "Alarm", render: (a) => <Link className="font-medium text-accent-strong underline" to={`/customers/${a.environmentId}/alarms`}>{a.alarmName}</Link> },
              ...(internal ? [{ key: "customer", header: "Customer", render: (a: (typeof f.criticalAlarms)[number]) => <Link className="underline" to={`/customers/${a.environmentId}`}>{a.friendlyName}</Link> }] : []),
              { key: "seen", header: "Last seen", render: (a) => <RelativeTime iso={a.lastSeenAt} /> },
              { key: "count", header: "Occurrences", numeric: true, render: (a) => a.occurrenceCount },
              { key: "ticket", header: "Ticket", render: (a) => (a.hasTicket ? "open" : "none") },
            ]}
          />
        </Card>

        <Card title="Quotas nearing breach">
          <DataTable
            caption="Quotas at or above 80 percent of their limit"
            rows={f.quotasNearLimit}
            rowKey={(q) => `${q.environmentId}:${q.serviceCode}:${q.quotaCode}`}

            sortBy={(q) => -q.usagePercent}
            empty={<EmptyState title="No quotas near their limit" />}
            columns={[
              { key: "quota", header: "Quota", render: (q) => <Link className="font-medium text-accent-strong underline" to={`/customers/${q.environmentId}/quotas`}>{q.quotaName}</Link> },
              ...(internal ? [{ key: "customer", header: "Customer", render: (q: (typeof f.quotasNearLimit)[number]) => q.friendlyName }] : []),
              { key: "usage", header: "Usage", numeric: true, render: (q) => `${q.usage} / ${q.value}` },
              { key: "pct", header: "%", numeric: true, render: (q) => `${q.usagePercent.toFixed(0)}%` },
              {
                key: "action",
                header: "",
                render: (q) =>
                  q.openRequestStatus ? (
                    // Already with AWS: say so rather than offer a second request, which the API would refuse anyway.
                    <Link to={`/customers/${q.environmentId}/quotas`} className="inline-flex items-center gap-1.5 whitespace-nowrap" title="An increase request is already open for this quota">
                      <StatusPill status={QUOTA_REQUEST_STATUS[q.openRequestStatus].label} tone={QUOTA_REQUEST_STATUS[q.openRequestStatus].tone} />
                    </Link>
                  ) : q.increaseRequestsAvailable && q.adjustable ? (
                    <Link className="underline" to={`/customers/${q.environmentId}/quotas`}>Request increase</Link>
                  ) : null,
              },
            ]}
          />
        </Card>

        {internal && f.accountsGoneQuiet && (
          <Card title="Accounts with no heartbeat" className="lg:col-span-2">
            <DataTable
              caption="Customers whose stack has stopped reporting"
              rows={f.accountsGoneQuiet}
              rowKey={(c) => c.environmentId}

              sortBy={(c) => c.friendlyName}
              empty={<EmptyState title="Every active account is reporting" />}
              columns={[
                { key: "customer", header: "Customer", render: (c) => <Link className="font-medium text-accent-strong underline" to={`/customers/${c.environmentId}`}>{c.friendlyName}</Link> },
                { key: "reason", header: "Why", render: (c) => ({ never_connected: "Never completed the connectivity test", no_heartbeat: "Connected but never heartbeat", heartbeat_stale: "Heartbeat stopped" })[c.reason] },
                { key: "hb", header: "Last heartbeat", render: (c) => <RelativeTime iso={c.lastHeartbeatAt} /> },
              ]}
            />
          </Card>
        )}
      </div>
    </>
  );
}
